import type { ListenerRole } from '../hooks/usePeers';

/**
 * Admin-side review state for a listener's profile photo.
 *
 * The API returns rejected photos in `proposed_profile_photo` so the peer-side
 * UI can prompt the listener to re-upload. From an admin's perspective, a
 * rejected photo has already been actioned and should NOT be treated like a
 * fresh "needs review" submission. Centralising that distinction here keeps
 * new admin surfaces from re-deriving the bug.
 */
export type AdminProfilePhotoState = 'no_photo' | 'pending_review' | 'rejected' | 'approved';

type ListenerPhotoFields = Pick<
  ListenerRole,
  'proposed_profile_photo' | 'profile_photo_approved' | 'current_profile_photo'
>;

export const getAdminProfilePhotoState = (peer: ListenerPhotoFields): AdminProfilePhotoState => {
  const proposedStatus = peer.proposed_profile_photo?.status;
  if (proposedStatus === 'pending') return 'pending_review';
  if (proposedStatus === 'rejected') return 'rejected';
  if (peer.current_profile_photo) return 'approved';
  return peer.profile_photo_approved ? 'approved' : 'no_photo';
};

/**
 * True when the peer has submitted a photo that an admin still needs to grade.
 * Use this for any "Pending: Profile Picture" / "Needs Review" indicator —
 * `!!peer.proposed_profile_photo` is wrong because it also matches rejected
 * photos that admins have already actioned.
 */
export const isAvatarPendingAdminReview = (peer: Pick<ListenerRole, 'proposed_profile_photo'>): boolean =>
  peer.proposed_profile_photo?.status === 'pending';
